const PURCHASE_EVENT_STORAGE_PREFIX = "purchase_event_sent:";
const PURCHASE_EVENT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface PurchaseEventRecord {
  sentAt: number;
}

function getPurchaseEventKey(channel: string, orderId: string): string {
  return `${PURCHASE_EVENT_STORAGE_PREFIX}${encodeURIComponent(channel)}:${encodeURIComponent(orderId)}`;
}

function getStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function parsePurchaseEventRecord(value: string | null): PurchaseEventRecord | null {
  if (!value) return null;

  // Backward-compatible with simple boolean markers if any caller had stored them.
  if (value === "true") {
    return { sentAt: Number.POSITIVE_INFINITY };
  }

  try {
    const parsed = JSON.parse(value) as Partial<PurchaseEventRecord>;
    return typeof parsed.sentAt === "number" ? { sentAt: parsed.sentAt } : null;
  } catch {
    return null;
  }
}

function isExpired(record: PurchaseEventRecord, now = Date.now()): boolean {
  return Number.isFinite(record.sentAt) && now - record.sentAt > PURCHASE_EVENT_TTL_MS;
}

function cleanupExpiredPurchaseEvents(storage: Storage): void {
  const now = Date.now();

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (!key?.startsWith(PURCHASE_EVENT_STORAGE_PREFIX)) continue;

    const record = parsePurchaseEventRecord(storage.getItem(key));
    if (!record || isExpired(record, now)) {
      storage.removeItem(key);
    }
  }
}

export function hasPurchaseEventSent(channel: string, orderId: string): boolean {
  try {
    const storage = getStorage();
    if (!storage) return false;

    const key = getPurchaseEventKey(channel, orderId);
    const record = parsePurchaseEventRecord(storage.getItem(key));

    if (!record || isExpired(record)) {
      storage.removeItem(key);
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

export function markPurchaseEventSent(channel: string, orderId: string): boolean {
  try {
    const storage = getStorage();
    if (!storage) return false;

    cleanupExpiredPurchaseEvents(storage);
    storage.setItem(
      getPurchaseEventKey(channel, orderId),
      JSON.stringify({ sentAt: Date.now() } satisfies PurchaseEventRecord)
    );
    return true;
  } catch {
    // localStorage may be unavailable in private mode or blocked browser contexts.
    return false;
  }
}
